import { sessionStore } from '$lib/api/session.svelte';
import { chatStore } from '$lib/chat/chat-store.svelte';
import { auth } from './auth.svelte';

/**
 * Sequence de logout standard imposee par l'ADR 018 : terminer la session backend (POST /stop)
 * AVANT de purger les tokens et rediriger vers Cognito /logout. Sans cet ordre, l'EC2 vit ~30
 * min jusqu'a ce que le sweep DDB la nettoie (gaspille du quota utilisateur).
 *
 * On passe par sessionStore.stop() (et non backendClient.stop() direct) pour que les timers
 * internes (heartbeat 15 min, polling /health, compteur elapsed) soient propres au logout.
 * sessionStore.stop() ignore les 404 SessionNotFoundError en interne.
 *
 * Tout composant qui declenche un logout DOIT utiliser cette fonction, pas auth.logout()
 * directement, pour garantir la coherence avec ADR 018.
 */
export async function fullLogout(): Promise<void> {
  // Leve le flag loggingOut AVANT sessionStore.stop() : son resetState() ecrit phase='idle',
  // qui re-queue le $effect du layout (phase est tracke transitivement via sessionStore.start()).
  // Sans ce flag deja leve, le $effect fire entre stop() et auth.logout() avec isAuthenticated=true
  // et loggingOut=false -> POST /start parasite observable dans la network tab juste apres /stop.
  auth.beginLogout();
  try {
    try {
      await sessionStore.stop();
    } catch (err) {
      console.warn('Session stop during logout (non-fatal):', err);
    }
    // Purge l'historique chat LLM AVANT auth.logout() : ce dernier (en mode real Cognito) va
    // navigate vers /logout et la page va etre detruite. Sans ce clear explicite, l'historique
    // sessionStorage `rmf:chat:history` ne survit certes pas a la fermeture de tab, mais peut
    // persister si l'user re-login dans la meme session browser (cas Cognito SSO court).
    chatStore.clear();
    await auth.logout();
    // En mock : auth.session = null, le $effect du +layout redirige vers /home automatiquement.
    // En real : auth.logout() a appele window.location.assign vers Cognito /logout, on quitte la page.
  } catch (err) {
    // Echec avant la navigation Cognito : reset le flag pour permettre un retry et eviter de
    // laisser l'app dans un etat "logout perpetuel" (tous les $effect autenfifies skippes).
    auth.cancelLogout();
    throw err;
  }
}
